/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useAuthCleanup } from './AuthCleanupContext';
import {
  subscribeToFavorites,
  subscribeToLists,
  subscribeToFavoritesCount,
  addFavorite as addFavoriteService,
  removeFavorite as removeFavoriteService,
  removeFavoriteByItem as removeFavoriteByItemService,
  toggleFavorite as toggleFavoriteService,
  createList as createListService,
  updateList as updateListService,
  deleteList as deleteListService,
  addToList as addToListService,
  removeFromList as removeFromListService,
  updateFavoriteCachedData as updateFavoriteCachedDataService,
} from '@/services/favorites';
import type {
  FavoriteItem,
  FavoriteList,
  FavoriteItemType,
  FavoriteCachedData,
  FavoriteSortOption,
  FavoriteFilterType,
} from '@/types/favorites';

// ==================== Context Types ====================

interface FavoritesContextValue {
  // State
  favorites: FavoriteItem[];
  lists: FavoriteList[];
  totalCount: number;
  loading: boolean;
  error: string | null;

  // Favorite actions
  addFavorite: (
    itemId: string,
    itemType: FavoriteItemType,
    cachedData: FavoriteCachedData
  ) => Promise<string>;
  removeFavorite: (favoriteId: string) => Promise<void>;
  removeFavoriteByItem: (itemId: string, itemType: FavoriteItemType) => Promise<void>;
  toggleFavorite: (
    itemId: string,
    itemType: FavoriteItemType,
    cachedData: FavoriteCachedData
  ) => Promise<{ favorited: boolean; favoriteId: string | null }>;
  isFavorited: (itemId: string, itemType: FavoriteItemType) => boolean;
  getFavoriteId: (itemId: string, itemType: FavoriteItemType) => string | null;
  updateCachedData: (favoriteId: string, cachedData: Partial<FavoriteCachedData>) => Promise<void>;

  // List actions
  createList: (name: string) => Promise<string>;
  updateList: (listId: string, name: string) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;
  addToList: (favoriteId: string, listId: string) => Promise<void>;
  removeFromList: (favoriteId: string, listId: string) => Promise<void>;

  // Filtering & Sorting
  getFilteredFavorites: (filter: FavoriteFilterType, listId?: string) => FavoriteItem[];
  getSortedFavorites: (favorites: FavoriteItem[], sortOption: FavoriteSortOption) => FavoriteItem[];
  searchFavorites: (query: string, favorites: FavoriteItem[]) => FavoriteItem[];
}

interface FavoritesProviderProps {
  children: ReactNode;
}

// ==================== Context ====================

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

// ==================== Provider ====================

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const { authState } = useAuth();
  const { registerCleanup } = useAuthCleanup();

  // State
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [lists, setLists] = useState<FavoriteList[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const unsubscribeFavorites = useRef<(() => void) | null>(null);
  const unsubscribeLists = useRef<(() => void) | null>(null);
  const unsubscribeCount = useRef<(() => void) | null>(null);

  // Get current user ID
  const userId = authState.status === 'authenticated' ? authState.user.uid : null;

  // Register cleanup function for auth logout
  useEffect(() => {
    registerCleanup('favorites-context', () => {
      // Unsubscribe from listeners
      unsubscribeFavorites.current?.();
      unsubscribeFavorites.current = null;
      unsubscribeLists.current?.();
      unsubscribeLists.current = null;
      unsubscribeCount.current?.();
      unsubscribeCount.current = null;
      // Reset state
      setFavorites([]);
      setLists([]);
      setTotalCount(0);
      setLoading(false);
      setError(null);
    });
  }, [registerCleanup]);

  // Create a map for quick lookup
  const favoritesMap = useMemo(() => {
    const map = new Map<string, FavoriteItem>();
    favorites.forEach((fav) => {
      const key = `${fav.itemType}:${fav.itemId}`;
      map.set(key, fav);
    });
    return map;
  }, [favorites]);

  // Subscribe to favorites when user is authenticated
  useEffect(() => {
    if (!userId) {
      setFavorites([]);
      setLists([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to favorites
    unsubscribeFavorites.current = subscribeToFavorites(userId, (items) => {
      setFavorites(items);
      setLoading(false);
    });

    // Subscribe to custom lists
    unsubscribeLists.current = subscribeToLists(userId, (items) => {
      setLists(items);
    });

    // Subscribe to count
    unsubscribeCount.current = subscribeToFavoritesCount(userId, (count) => {
      setTotalCount(count);
    });

    return () => {
      unsubscribeFavorites.current?.();
      unsubscribeLists.current?.();
      unsubscribeCount.current?.();
    };
  }, [userId]);

  // ==================== Favorite Actions ====================

  const addFavorite = useCallback(
    async (
      itemId: string,
      itemType: FavoriteItemType,
      cachedData: FavoriteCachedData
    ): Promise<string> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        return await addFavoriteService(userId, itemId, itemType, cachedData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add favorite';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const removeFavorite = useCallback(
    async (favoriteId: string): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await removeFavoriteService(userId, favoriteId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove favorite';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const removeFavoriteByItem = useCallback(
    async (itemId: string, itemType: FavoriteItemType): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await removeFavoriteByItemService(userId, itemId, itemType);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove favorite';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const toggleFavorite = useCallback(
    async (
      itemId: string,
      itemType: FavoriteItemType,
      cachedData: FavoriteCachedData
    ): Promise<{ favorited: boolean; favoriteId: string | null }> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        return await toggleFavoriteService(userId, itemId, itemType, cachedData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to toggle favorite';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const isFavorited = useCallback(
    (itemId: string, itemType: FavoriteItemType): boolean => {
      const key = `${itemType}:${itemId}`;
      return favoritesMap.has(key);
    },
    [favoritesMap]
  );

  const getFavoriteId = useCallback(
    (itemId: string, itemType: FavoriteItemType): string | null => {
      const key = `${itemType}:${itemId}`;
      const favorite = favoritesMap.get(key);
      return favorite?.id || null;
    },
    [favoritesMap]
  );

  const updateCachedData = useCallback(
    async (favoriteId: string, cachedData: Partial<FavoriteCachedData>): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await updateFavoriteCachedDataService(userId, favoriteId, cachedData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update cached data';
        console.error('Failed to update cached data:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    [userId]
  );

  // ==================== List Actions ====================

  const createList = useCallback(
    async (name: string): Promise<string> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        return await createListService(userId, name);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create list';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const updateList = useCallback(
    async (listId: string, name: string): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await updateListService(userId, listId, name);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update list';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const deleteList = useCallback(
    async (listId: string): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await deleteListService(userId, listId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete list';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const addToList = useCallback(
    async (favoriteId: string, listId: string): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await addToListService(userId, favoriteId, listId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add to list';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  const removeFromList = useCallback(
    async (favoriteId: string, listId: string): Promise<void> => {
      if (!userId) {
        throw new Error('User not authenticated');
      }

      try {
        await removeFromListService(userId, favoriteId, listId);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove from list';
        setError(errorMessage);
        throw err;
      }
    },
    [userId]
  );

  // ==================== Filtering & Sorting ====================

  const getFilteredFavorites = useCallback(
    (filter: FavoriteFilterType, listId?: string): FavoriteItem[] => {
      let filtered = favorites;

      // Filter by type
      if (filter !== 'all') {
        filtered = filtered.filter((fav) => fav.itemType === filter);
      }

      // Filter by list
      if (listId) {
        filtered = filtered.filter((fav) => fav.listIds.includes(listId));
      }

      return filtered;
    },
    [favorites]
  );

  const getSortedFavorites = useCallback(
    (items: FavoriteItem[], sortOption: FavoriteSortOption): FavoriteItem[] => {
      const sorted = [...items];

      switch (sortOption) {
        case 'date_added_desc':
          sorted.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
          break;
        case 'date_added_asc':
          sorted.sort((a, b) => {
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return aTime - bTime;
          });
          break;
        case 'item_date_desc':
          sorted.sort((a, b) => {
            const aTime = a.cachedData.itemCreatedAt?.toMillis?.() || 0;
            const bTime = b.cachedData.itemCreatedAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
          break;
        case 'item_date_asc':
          sorted.sort((a, b) => {
            const aTime = a.cachedData.itemCreatedAt?.toMillis?.() || 0;
            const bTime = b.cachedData.itemCreatedAt?.toMillis?.() || 0;
            return aTime - bTime;
          });
          break;
        case 'alpha_asc':
          sorted.sort((a, b) => (a.cachedData.title || '').localeCompare(b.cachedData.title || ''));
          break;
        case 'alpha_desc':
          sorted.sort((a, b) => (b.cachedData.title || '').localeCompare(a.cachedData.title || ''));
          break;
      }

      return sorted;
    },
    []
  );

  const searchFavorites = useCallback((query: string, items: FavoriteItem[]): FavoriteItem[] => {
    if (!query.trim()) {
      return items;
    }

    const lowerQuery = query.toLowerCase().trim();

    return items.filter((fav) => {
      const title = fav.cachedData.title?.toLowerCase() || '';
      const preview = fav.cachedData.preview?.toLowerCase() || '';
      const author = fav.cachedData.authorName?.toLowerCase() || '';

      return (
        title.includes(lowerQuery) || preview.includes(lowerQuery) || author.includes(lowerQuery)
      );
    });
  }, []);

  // ==================== Context Value ====================

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo<FavoritesContextValue>(
    () => ({
      // State
      favorites,
      lists,
      totalCount,
      loading,
      error,

      // Favorite actions
      addFavorite,
      removeFavorite,
      removeFavoriteByItem,
      toggleFavorite,
      isFavorited,
      getFavoriteId,
      updateCachedData,

      // List actions
      createList,
      updateList,
      deleteList,
      addToList,
      removeFromList,

      // Filtering & Sorting
      getFilteredFavorites,
      getSortedFavorites,
      searchFavorites,
    }),
    [
      favorites,
      lists,
      totalCount,
      loading,
      error,
      addFavorite,
      removeFavorite,
      removeFavoriteByItem,
      toggleFavorite,
      isFavorited,
      getFavoriteId,
      updateCachedData,
      createList,
      updateList,
      deleteList,
      addToList,
      removeFromList,
      getFilteredFavorites,
      getSortedFavorites,
      searchFavorites,
    ]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

// ==================== Hooks ====================

export function useFavorites(): FavoritesContextValue {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}

/**
 * Hook to check if a specific item is favorited
 */
export function useIsFavorited(itemId: string, itemType: FavoriteItemType): boolean {
  const { isFavorited } = useFavorites();
  return isFavorited(itemId, itemType);
}

/**
 * Hook to get favorites of a specific type
 */
export function useFavoritesByType(itemType: FavoriteItemType): FavoriteItem[] {
  const { favorites } = useFavorites();
  return useMemo(() => favorites.filter((fav) => fav.itemType === itemType), [favorites, itemType]);
}

/**
 * Hook to get favorites in a specific list
 */
export function useFavoritesInList(listId: string): FavoriteItem[] {
  const { favorites } = useFavorites();
  return useMemo(
    () => favorites.filter((fav) => fav.listIds.includes(listId)),
    [favorites, listId]
  );
}
